import { Logger, Utils } from "neo-common";
import { ClientHost, GameEngine, Communication } from "neo-games";

import { Dialog, DialogOpenData } from "../Channels/Common/Dialogs/Dialog";
import { GameManager } from "../Managers/GameManager";
import { BalanceManager } from "../Managers/BalanceManager";
import { ModuleManager } from "../Managers/ModuleManager";
import { BetManager } from "../Managers/BetManager";
import { DataManager } from "../Managers/DataManager";
import { Languages } from "../Utils/Languages";
import { HtmlController } from "../HTMLComponents/HtmlController";
import { SplashScreen } from "../CustomComponents/SplashScreen";

type IBalance = Communication.Game.IBalance;

export class GameClientHostConsumer extends GameEngine.GameClientHostConsumerBase {
    private _dialog: Dialog;

    constructor(app: GameEngine.IApp) {
        super(app);
    }

    private get dialog(): Dialog {
        if (Utils.isNullOrUndefined(this._dialog)) {
            this._dialog = new Dialog(this._app);
        }

        return this._dialog;
    }

    /**
     * Handle dispatched action from client host
     * @param actionGroup - group name where action belongs to
     * @param action - name of action to handle
     */
    handleAction(actionGroup: string, action: string): void {
        if (!HtmlController.dispatchActionAllowed) {
            return;
        }

        if (actionGroup === ClientHost.Types.ClientHostActionGroup.Default.toString()) {
            if (DEBUG) {
                Logger.debugGroup(
                    "GameClientHostConsumer",
                    `Received action "${action}" (group: '${actionGroup}'), dispatching to controller.dispatchAction() ...`,
                );
            }

            DataManager.addTrustedUserAction(action);
            ModuleManager.dispatchAction(action);
        } else if (actionGroup === ClientHost.Types.ClientHostActionGroup.Terminal.toString()) {
            if (DEBUG) {
                Logger.debugGroup(
                    "GameClientHostConsumer",
                    `Received action "${action}" (group: '${actionGroup}'), dispatching to controller.dispatchTerminalAction() ...`,
                );
            }

            DataManager.addTrustedUserAction(action);
            ModuleManager.dispatchAction(action);
        }
    }

    /**
     * handle request to show dialog with message (optionally error)
     * @param request - show dialog request data
     */
    handleShowDialog(request: ClientHost.Types.ClientHostDialogRequest): void {
        if (DEBUG) {
            Logger.debugGroup("GameClientHostConsumer", `inside handleShowDialog(), request: `, request);
        }

        if (request.type === ClientHost.Types.ClientHostDialogType.Responsible1) {
            this.showResponsibleDialog1(request.data as ClientHost.Types.ClientHostResponsibleDialogData);
        } else {
            let dialogData = Utils.isNullOrUndefined(request.data) ? null : (request.data as DialogOpenData);
            let special = false;
            let gameRoundId = "0";
            let errorId = "";
            if (!Utils.isNullOrUndefined(dialogData)) {
                special = dialogData.special;
                gameRoundId = dialogData.gameRoundId;
                errorId = dialogData.errorId;
            }
            let isError = request.type === ClientHost.Types.ClientHostDialogType.Error;
            let code = request.error ? request.error.errorCode : "";

            SplashScreen.hideSplashScreen();
            this.dialog.openDialog(request.text, code, errorId, isError, special, gameRoundId, request);
        }
    }

    /**
     * handle request to show dialog with message
     */
    handleShowEndGameDialog(): void {
        //dialog without buttons
        let customData = {
            data: {
                actions: [null] as Array<Object>,
            },
        };

        this.dialog.openDialog(Languages.getRid("DLG_ENDGAME"), "", "", false, true, "", customData);
        PIXI.sound.removeAll();

        //remove game and keep only dialog
        let dialog = document.getElementById("dialog");
        let root = document.getElementById("root");
        if (root != null && dialog != null) {
            root.innerHTML = dialog.outerHTML;
            document.body.innerHTML = "";
            document.body.appendChild(root);
        }
    }

    /**
     * handle request to close currently visible dialog (if any)
     */
    handleCloseDialog(): void {
        this.dialog.closeDialog();
    }

    /**
     * @method handleRefreshAccount - handle 'refresh account' request from client host
     */
    handleRefreshAccount(): void {
        BalanceManager.refreshBalance();
    }

    /**
     * @method handleError - handle error from client host.
     * @param error - error message
     * @param data - structure with information about error
     */
    handleError(error: string, data?: any): void {
        this.dialog.openDialog(error, null, "", true);
    }

    handleBlockRequest(blocked: boolean): void {
        if (blocked) {
            if (DataManager.Autoplay) {
                DataManager.gameState("AutoPlayEnded");
            }
            DataManager.Autoplay = false;
            DataManager.AutoplayCount = "∞";
            DataManager.AutoplayLossLimit = "∞";
            DataManager.AutoplayWinLimit = "∞";
        }
    }

    /**
     * @method handlePaytableAction - handle paytable action (load, show, ...)
     * @param request - request to do with paytable
     */
    handlePaytableRequest(request: ClientHost.Types.ClientHostPaytableRequest): void {}

    handleAccountUpdate(balance: IBalance, forceShow: boolean): void {
        if (forceShow) {
            BetManager.unHideValues(true);
        }

        BalanceManager.balanceReceived(balance);
    }

    handleAutoplayAction(action: ClientHost.Types.AutoplayAction): void {
        switch (action) {
            case ClientHost.Types.AutoplayAction.Stop: {
                if (DataManager.Autoplay) {
                    DataManager.gameState("AutoPlayEnded");
                }
                DataManager.Autoplay = false;
                DataManager.AutoplayCount = "∞";
                DataManager.AutoplayLossLimit = "∞";
                DataManager.AutoplayWinLimit = "∞";
                break;
            }
            case ClientHost.Types.AutoplayAction.Enable: {
                DataManager.AutoplayEnabled = true;
                break;
            }
            case ClientHost.Types.AutoplayAction.Disable: {
                if (DataManager.Autoplay) {
                    DataManager.gameState("AutoPlayEnded");
                }
                DataManager.Autoplay = false;
                DataManager.AutoplayCount = "∞";
                DataManager.AutoplayLossLimit = "∞";
                DataManager.AutoplayWinLimit = "∞";
                DataManager.AutoplayEnabled = false;
                break;
            }
            default: {
                if (DEBUG) {
                    Logger.errorGroup("GameClientHostConsumer", `Unknown autoplay action '${action}'`);
                }
                break;
            }
        }
    }

    handleGameplayAction(action: ClientHost.Types.GamePlayAction): void {
        switch (action) {
            case ClientHost.Types.GamePlayAction.Enable: {
                GameManager.enablePlay();
                break;
            }
            case ClientHost.Types.GamePlayAction.Disable: {
                GameManager.disablePlay();
                break;
            }
            default: {
                if (DEBUG) {
                    Logger.errorGroup("GameClientHostConsumer", `Unknown gameplay action '${action}'`);
                }
                break;
            }
        }
    }

    terminateOnCollect(): boolean {
        if (DEBUG) {
            Logger.debugGroup("GameClientHostConsumer", `inside terminateOnCollect()`);
        }

        return GameManager.canExit(true);
    }

    private showResponsibleDialog1(data: ClientHost.Types.ClientHostResponsibleDialogData) {
        if (DEBUG) {
            Logger.debugGroup("GameClientHostConsumer", `showResponsibleDialog1(), data: `, data);
        }

        GameManager.showResponsibleDialog1(data);
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/ClientHost/ClientHostConsumer.ts
 **/